import { FC, useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useConversations from '@/api/useConversations.ts';
import useMessages from '@/api/useMessages.ts';
import useSendMessage from '@/api/useSendMessage.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Routes from '@/router/Routes.ts';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import type { Conversation } from '@/api/useConversations.ts';

dayjs.extend(relativeTime);

const columnHelper = createColumnHelper<Conversation>();

const MessagesPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(userId);
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: conversations, isLoading: loadingConversations } = useConversations();
  const { data: messages, isLoading: loadingMessages } = useMessages(selectedUserId);
  const { mutate: sendMessage, isPending: isSending } = useSendMessage();

  const selectedConversation = conversations?.find(c => c.user_id === selectedUserId);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Update selected user when URL changes
  useEffect(() => {
    if (userId) {
      setSelectedUserId(userId);
    }
  }, [userId]);

  const handleSelectConversation = (userId: string) => {
    setSelectedUserId(userId);
    navigate(Routes.MESSAGES_WITH_USER.replace(':userId', userId));
  };

  const handleBackToList = () => {
    setSelectedUserId(undefined);
    navigate(Routes.MESSAGES);
  };

  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!messageInput.trim() || !selectedUserId || isSending) return;

    sendMessage(
      {
        receiver_id: selectedUserId,
        content: messageInput.trim(),
      },
      {
        onSuccess: () => {
          setMessageInput('');
        },
      }
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch {
      return '';
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('user_name', {
        header: 'Name',
        cell: (info) => {
          const conversation = info.row.original;
          const hasUnread = conversation.unread_count > 0;
          return (
            <div className="flex items-center gap-4">
              <div className="relative w-12 h-12 flex-shrink-0">
                <div className="w-12 h-12 rounded-lg overflow-hidden">
                  {conversation.user_photo ? (
                    <img
                      src={conversation.user_photo}
                      alt={conversation.user_name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 font-bold">
                      {conversation.user_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                {hasUnread && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#FED32C] rounded-full border-2 border-white" />
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <span className={hasUnread ? 'font-bold text-base text-black' : 'font-semibold text-base text-gray-700'}>
                  {info.getValue()}
                </span>
                {conversation.last_message && (
                  <span className={`text-xs truncate max-w-[200px] ${hasUnread ? 'text-black font-medium' : 'text-gray-400'}`}>
                    {conversation.last_message}
                  </span>
                )}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'action',
        header: '',
        cell: (info) => {
          const conversation = info.row.original;
          const hasUnread = conversation.unread_count > 0;
          return (
            <div className="flex items-center justify-end gap-2">
              {hasUnread && (
                <span className="bg-black text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                  {conversation.unread_count}
                </span>
              )}
              <button
                onClick={() => handleSelectConversation(conversation.user_id)}
                className="bg-[#2B3440] text-white hover:opacity-80 rounded-lg py-[8px] px-[10px] text-xs sm:text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                show message
              </button>
            </div>
          );
        },
      }),
    ],
    [handleSelectConversation]
  );

  // Show conversation view if userId is selected
  if (selectedUserId) {
    return (
      <div className="flex flex-col h-[calc(100vh-120px)] sm:h-[80vh] w-full">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <button 
            onClick={handleBackToList}
            className="w-10 h-10 rounded-[12px] bg-black flex items-center justify-center text-white hover:opacity-80 cursor-pointer flex-shrink-0"
          >
            <img src="/assets/arrowLeft.svg" alt="back" style={{ width: '16.5px', height: '15px' }} />
          </button>
          {selectedConversation && (
            <span className="font-semibold text-base truncate">{selectedConversation.user_name}</span>
          )}
        </div>

        {/* Chat Window */}
        <div 
          className="flex-1 bg-white rounded-3xl flex flex-col overflow-hidden min-h-0"
          style={{ boxShadow: '0px 0px 12px 0px #0000000D' }}
        >
          {/* Messages */}
          <div ref={messagesEndRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
            {loadingMessages ? (
              <div className="text-center text-gray-500">Loading messages...</div>
            ) : messages?.length === 0 ? (
              <></>
            ) : (
              messages?.map((message) => {
                const isOwnMessage = message.sender_id === session?.user?.id;
                return (
                  <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                    <div className="flex items-end space-x-3 max-w-xs lg:max-w-md">
                      {!isOwnMessage && (
                        <div className="w-10 h-10 flex-shrink-0">
                          <div className="w-10 h-10 rounded-full overflow-hidden">
                            {selectedConversation?.user_photo ? (
                              <img
                                src={selectedConversation.user_photo}
                                alt={selectedConversation.user_name}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-500 font-bold">
                                {selectedConversation?.user_name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div
                          className={`flex items-center space-x-2 mb-1 ${isOwnMessage ? 'flex-row-reverse space-x-reverse' : ''}`}
                        >
                          <span className="text-sm font-medium text-gray-700">
                            {isOwnMessage
                              ? session?.user?.user_metadata?.name || 'You'
                              : selectedConversation?.user_name}
                          </span>
                          <span className="text-xs text-gray-500">{formatTimestamp(message.created_at)}</span>
                        </div>
                        <div
                          className={`px-4 py-3 rounded-2xl max-w-sm ${
                            isOwnMessage
                              ? 'bg-[#2B3440] rounded-br-md'
                              : 'bg-[#2B3440] rounded-bl-md'
                          }`}
                          style={{ color: '#D7DDE4' }}
                        >
                          <div className="text-sm leading-relaxed">{message.content}</div>
                        </div>
                      </div>
                      {isOwnMessage && (
                        <div className="w-10 h-10 flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-[#EDBE39]"></div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Message Input */}
          <div className="border-t border-gray-100 p-4">
            <div className="flex items-center space-x-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Type your message"
                  disabled={isSending}
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!messageInput.trim() || isSending}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-500 disabled:opacity-50 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show conversations list
  return (
    <div className="flex flex-col w-full">
      <h1 className="text-2xl font-bold mb-6">Keep messages short and practical</h1>

      {loadingConversations ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : !conversations || conversations.length === 0 ? (
        <div className="bg-[#F6F6F4] rounded-lg py-6 flex items-center justify-center w-full mt-8">
          <p className="text-base font-semibold text-center text-black">
            No conversations yet
          </p>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <DataTable
            data={conversations}
            columns={columns}
            wrapperClassName="border border-gray-200 rounded-lg min-w-[320px]"
            rowClassName="bg-[#F6F6F4]"
            headClassName="bg-[#F6F6F4]"
          />
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
